import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { BeachLoader } from "@/components/ui/BeachLoader";

// Name + role are required to use the app. Avatar is intentionally optional,
// Onboarding bails out without one (see Onboarding.tsx), so this predicate
// must agree or routes like /profile become unreachable for users who
// skipped the photo step. Cached for the session (Oct 17: the check used to
// run again, and block the page, on every protected navigation).
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const check = useQuery({
    queryKey: ["profile-check", user?.id],
    enabled: !!user,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("display_name, user_types")
        .eq("user_id", user!.id)
        .single();
      const hasName = !!data?.display_name?.trim();
      const types = (data?.user_types as string[]) ?? [];
      return hasName && types.length > 0;
    },
  });

  if (loading || (user && check.data === undefined)) {
    return <BeachLoader className="min-h-screen" />;
  }

  // Preserve where the user was headed so auth/onboarding can return them
  // (e.g. the still-looking email lands on /profile?msthSurvey=1 signed out).
  const returnTo = encodeURIComponent(location.pathname + location.search);

  if (!user) {
    return <Navigate to={`/login?mode=signin&redirect=${returnTo}`} replace />;
  }

  // Mandatory: full name + user type before accessing the app.
  if (!check.data && location.pathname !== "/onboarding") {
    return <Navigate to={`/onboarding?redirect=${returnTo}`} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
